import React, { useState } from 'react';
import { Layout } from '../components/layout';
import { toast } from 'react-toastify';
import { AlertCircle, Flag } from 'lucide-react';
import api from '../utils/api';

const Takedown = () => {
  const [formData, setFormData] = useState({
    recordId: '',
    reason: '',
    email: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recordId || !formData.reason || !formData.email) {
      toast.error('Please fill in all required fields including email');
      return;
    }

    setSubmitting(true);

    try {
      await api.post('/takedown', {
        record_id: formData.recordId,
        reason: formData.reason,
        email: formData.email || null
      });
      
      toast.success('Takedown request submitted. We will review within 48 hours.');
      setFormData({ recordId: '', reason: '', email: '' });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="text-center mb-8">
          <h1 className="font-headline text-4xl md:text-5xl text-white mb-2">
            TAKEDOWN PORTAL
          </h1>
          <p className="font-mono text-xs text-gray-500 tracking-[0.2em] uppercase">
            Report Privacy or Rights Violations
          </p>
        </div>

        {/* Warning Notice */}
        <div className="bg-red-900/20 border border-red-900/50 rounded p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-mono text-sm text-red-400 leading-relaxed">
                LEGITIMATE REMOVAL REQUESTS INCLUDE: PRIVACY VIOLATIONS, EXPOSURE OF PII, OR COPYRIGHT INFRINGEMENT. 
                <span className="block mt-2 text-red-500">
                  DISAGREEMENT WITH POLITICAL SENTIMENT OR MUNICIPAL GRIEVANCES IS NOT A VALID BASIS FOR REMOVAL.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Takedown Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Record ID */}
          <div>
            <label className="block font-mono text-xs text-gray-400 tracking-wider mb-2 uppercase">
              Ledger Record ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.recordId}
              onChange={(e) => setFormData({ ...formData, recordId: e.target.value })}
              required
              className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors"
              placeholder="Ledger Record ID (e.g. g1736...)"
            />
            <p className="font-mono text-[10px] text-yellow-500 mt-1">
              ⚠️ IMPORTANT: The Record ID must be exact. Find it in the Ledger page - incorrect IDs cannot be processed.
            </p>
          </div>

          {/* Reason */}
          <div>
            <label className="block font-mono text-xs text-gray-400 tracking-wider mb-2 uppercase">
              Reason for Removal Request
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              required
              rows={6}
              className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none resize-none transition-colors"
              placeholder="Reason for removal request..."
            />
          </div>

          {/* Contact Email (Required) */}
          <div>
            <label className="block font-mono text-xs text-gray-400 tracking-wider mb-2 uppercase">
              Contact Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors"
              placeholder="your@email.com"
            />
            <p className="font-mono text-[10px] text-gray-600 mt-1">
              Required so we can contact you about your request status.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white font-display text-sm py-4 tracking-wider transition-colors flex items-center justify-center gap-2"
            data-testid="submit-takedown-btn"
          >
            <Flag className="w-4 h-4" />
            {submitting ? 'SUBMITTING...' : 'SUBMIT REMOVAL REQUEST'}
          </button>
        </form>

        {/* Additional Info */}
        <div className="mt-12 pt-8 border-t border-[#1a1a1a]">
          <h3 className="font-display text-sm text-white tracking-wider mb-4">
            WHAT HAPPENS NEXT?
          </h3>
          <ul className="space-y-2 text-gray-400 font-mono text-xs">
            <li className="flex items-start gap-2">
              <span className="text-[#C5A52A]">1.</span>
              <span>Your request will be reviewed by our moderation team within 48 hours.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#C5A52A]">2.</span>
              <span>If the request is valid, the content will be removed or redacted.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#C5A52A]">3.</span>
              <span>If denied, you may appeal by contacting legal@accountability.works</span>
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default Takedown;
